import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Type, Image as ImageIcon, Minus, Trash2, Loader2, Eye, Printer,
  Bold, Italic, AlignLeft, AlignCenter, AlignRight, ZoomIn, ZoomOut, Maximize,
  ImageOff, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import DocumentCanvasView from "./DocumentCanvasView";
import DocumentTransactionsService from "./DocumentTransactions.service";
import { backgroundLayerStyle, compressBackgroundImage, printElement } from "./documentRender.utils";
import {
  CANVAS_PRESETS, FONT_FAMILIES, SAMPLE_PREVIEW_USER, DEFAULT_FOCAL_POINT,
  DEFAULT_BACKGROUND_OPACITY, MAX_BACKGROUND_IMAGE_SIZE, MAX_ORIGINAL_BACKGROUND_UPLOAD_SIZE,
  ACCEPTED_BACKGROUND_IMAGE_TYPES,
} from "./Documents.types";
import type {
  DocumentBackground, DocumentTemplate, TemplateElement, TextElement, WildcardElement, WildcardKey,
} from "./Documents.types";

function uid(prefix: string) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function defaultBackground(): DocumentBackground {
  return {
    url: "", width: 794, height: 1123, fit: "cover",
    focalPoint: { ...DEFAULT_FOCAL_POINT }, opacity: DEFAULT_BACKGROUND_OPACITY,
  };
}

// Firebase Functions payload shape for the background config — logged so it
// can be reviewed before the real backend endpoint exists.
function logBackgroundPayload(background: DocumentBackground) {
  console.log("[DocumentTemplate] background payload", {
    url: background.url,
    width: background.width,
    height: background.height,
    fit: background.fit,
    focalPoint: background.focalPoint,
    opacity: background.opacity,
  });
}

// 8 handles: corners scale proportionally (keeps the page's aspect ratio —
// the "resize" case), edges stretch a single axis (the "reshape" case).
// Matches the resize-handle convention used by Canva/Figma/PowerPoint.
const RESIZE_HANDLES: { key: string; top: number; left: number; cursor: string; corner: boolean }[] = [
  { key: "nw", top: 0, left: 0, cursor: "nwse-resize", corner: true },
  { key: "n", top: 0, left: 50, cursor: "ns-resize", corner: false },
  { key: "ne", top: 0, left: 100, cursor: "nesw-resize", corner: true },
  { key: "e", top: 50, left: 100, cursor: "ew-resize", corner: false },
  { key: "se", top: 100, left: 100, cursor: "nwse-resize", corner: true },
  { key: "s", top: 100, left: 50, cursor: "ns-resize", corner: false },
  { key: "sw", top: 100, left: 0, cursor: "nesw-resize", corner: true },
  { key: "w", top: 50, left: 0, cursor: "ew-resize", corner: false },
];

function isTextLike(el: TemplateElement): el is TextElement | WildcardElement {
  return el.type === "text" || el.type === "wildcard";
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: DocumentTemplate | null;
  onSaved: () => void;
};

export default function TemplateEditor({ open, onOpenChange, template, onSaved }: Props) {
  const service = useRef(new DocumentTransactionsService());

  const [ name, setName ] = useState("");
  const [ description, setDescription ] = useState("");
  const [ background, setBackground ] = useState<DocumentBackground>(defaultBackground());
  const [ elements, setElements ] = useState<TemplateElement[]>([]);
  const [ selectedId, setSelectedId ] = useState<string | null>(null);
  const [ saving, setSaving ] = useState(false);
  // The picked file is held locally and only actually uploaded on Save — so
  // changing your mind about the image (or closing without saving) never
  // leaves an orphaned file in Storage. `background.url` becomes a local
  // blob: preview in the meantime.
  const [ pendingBackgroundFile, setPendingBackgroundFile ] = useState<File | null>(null);
  const [ backgroundError, setBackgroundError ] = useState<string | null>(null);
  const [ previewOpen, setPreviewOpen ] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const [ canvasWrapWidth, setCanvasWrapWidth ] = useState(900);
  const [ canvasWrapHeight, setCanvasWrapHeight ] = useState(600);
  const [ zoom, setZoom ] = useState(1);

  useLayoutEffect(() => {
    if (!open) return;
    const el = canvasWrapRef.current;
    if (!el) return;
    // Measure immediately (avoids a flash at the stale default size), then
    // keep tracking via ResizeObserver as the dialog/window resizes.
    const rect = el.getBoundingClientRect();
    setCanvasWrapWidth(rect.width);
    setCanvasWrapHeight(rect.height);
    const observer = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) { setCanvasWrapWidth(r.width); setCanvasWrapHeight(r.height); }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ open ]);

  useEffect(() => {
    if (!open) return;
    // Starting fresh — release any unsaved local preview from a previous
    // session so we never leak blob: URLs.
    setBackground((prev) => { if (prev.url.startsWith("blob:")) URL.revokeObjectURL(prev.url); return prev; });
    setPendingBackgroundFile(null);
    if (template) {
      setName(template.name);
      setDescription(template.description || "");
      setBackground(template.background);
      setElements(template.elements);
    } else {
      setName("");
      setDescription("");
      setBackground(defaultBackground());
      setElements([]);
    }
    setSelectedId(null);
    setZoom(1);
  }, [ open, template ]);

  // canvasWrapRef measures the unpadded content area directly (padding lives
  // on its parent), so this is the true available space — just a small
  // breathing-room buffer so the page doesn't touch the edges.
  const breathingRoom = 16;
  const fitScale = Math.max(0.1, Math.min(
    (canvasWrapWidth - breathingRoom) / background.width,
    (canvasWrapHeight - breathingRoom) / background.height,
  ));
  const scale = fitScale * zoom;

  const zoomIn = () => setZoom((z) => Math.min(3, +(z + 0.1).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(0.25, +(z - 0.1).toFixed(2)));
  const zoomReset = () => setZoom(1);

  const handleCanvasWheel = (e: React.WheelEvent) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    setZoom((z) => Math.max(0.25, Math.min(3, +(z - e.deltaY * 0.001).toFixed(2))));
  };
  const selected = elements.find((e) => e.id === selectedId) || null;
  const nextZ = () => (elements.length ? Math.max(...elements.map((e) => e.zIndex)) + 1 : 1);

  // ── ELEMENT MUTATION ──────────────────────────────────────────────────
  const updateElement = (id: string, patch: Record<string, unknown>) => {
    setElements((prev) => prev.map((e) => (e.id === id ? ({ ...e, ...patch } as TemplateElement) : e)));
  };

  const addText = () => {
    const el: TextElement = {
      id: uid("el"), type: "text", x: 40, y: 40, width: 300, height: 40, rotation: 0, zIndex: nextZ(),
      content: "New text", fontFamily: "Inter", fontSize: 16, fontWeight: "normal", italic: false,
      color: "#1a1c1e", align: "left",
    };
    setElements((prev) => [ ...prev, el ]);
    setSelectedId(el.id);
  };

  const addWildcard = (key: WildcardKey) => {
    const el: WildcardElement = {
      id: uid("el"), type: "wildcard", wildcardKey: key, x: 40, y: 100, width: 300, height: 40, rotation: 0,
      zIndex: nextZ(), fontFamily: "Inter", fontSize: 18, fontWeight: "bold", italic: false,
      color: "#1a1c1e", align: "left",
    };
    setElements((prev) => [ ...prev, el ]);
    setSelectedId(el.id);
  };

  const addLine = () => {
    const el = {
      id: uid("el"), type: "line" as const, x: 40, y: 160, width: 220, height: 2, rotation: 0,
      zIndex: nextZ(), color: "#666666",
    };
    setElements((prev) => [ ...prev, el ]);
    setSelectedId(el.id);
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    setElements((prev) => prev.filter((e) => e.id !== selectedId));
    setSelectedId(null);
  };

  // ── DRAG / RESIZE ─────────────────────────────────────────────────────
  const handleDragStart = (e: React.PointerEvent, el: TemplateElement) => {
    e.stopPropagation();
    setSelectedId(el.id);
    const target = e.currentTarget as HTMLDivElement;
    target.setPointerCapture(e.pointerId);
    const startX = e.clientX;
    const startY = e.clientY;
    const origX = el.x;
    const origY = el.y;
    const onMove = (ev: PointerEvent) => {
      const dx = (ev.clientX - startX) / scale;
      const dy = (ev.clientY - startY) / scale;
      updateElement(el.id, { x: Math.max(0, origX + dx), y: Math.max(0, origY + dy) });
    };
    const onUp = () => {
      target.removeEventListener("pointermove", onMove);
      target.removeEventListener("pointerup", onUp);
    };
    target.addEventListener("pointermove", onMove);
    target.addEventListener("pointerup", onUp);
  };

  const handleResizeStart = (e: React.PointerEvent, el: TemplateElement) => {
    e.stopPropagation();
    const target = e.currentTarget as HTMLDivElement;
    target.setPointerCapture(e.pointerId);
    const startX = e.clientX;
    const startY = e.clientY;
    const origW = el.width;
    const origH = el.height;
    const onMove = (ev: PointerEvent) => {
      const dw = (ev.clientX - startX) / scale;
      const dh = (ev.clientY - startY) / scale;
      updateElement(el.id, { width: Math.max(20, origW + dw), height: Math.max(16, origH + dh) });
    };
    const onUp = () => {
      target.removeEventListener("pointermove", onMove);
      target.removeEventListener("pointerup", onUp);
    };
    target.addEventListener("pointermove", onMove);
    target.addEventListener("pointerup", onUp);
  };

  const handleBackgroundHandleResize = (e: React.PointerEvent, handle: string) => {
    e.stopPropagation();
    const target = e.currentTarget as HTMLDivElement;
    target.setPointerCapture(e.pointerId);
    const startX = e.clientX;
    const startY = e.clientY;
    const origW = background.width;
    const origH = background.height;
    const isCorner = handle.length === 2;
    const wSign = handle.includes("e") ? 1 : handle.includes("w") ? -1 : 0;
    const hSign = handle.includes("s") ? 1 : handle.includes("n") ? -1 : 0;

    const onMove = (ev: PointerEvent) => {
      const dx = (ev.clientX - startX) / scale;
      const dy = (ev.clientY - startY) / scale;
      if (isCorner) {
        // Proportional scale — driven by whichever axis moved further, signed
        // per corner, so the page's aspect ratio never changes.
        const driveDelta = Math.abs(dx) > Math.abs(dy) ? dx * wSign : dy * hSign;
        const base = Math.max(origW, origH);
        const factor = Math.max(0.1, (base + driveDelta) / base);
        setBackground((prev) => ({ ...prev, width: Math.max(100, origW * factor), height: Math.max(100, origH * factor) }));
      } else {
        setBackground((prev) => ({
          ...prev,
          width: wSign !== 0 ? Math.max(100, origW + dx * wSign) : prev.width,
          height: hSign !== 0 ? Math.max(100, origH + dy * hSign) : prev.height,
        }));
      }
    };
    const onUp = () => {
      target.removeEventListener("pointermove", onMove);
      target.removeEventListener("pointerup", onUp);
      setBackground((prev) => { logBackgroundPayload(prev); return prev; });
    };
    target.addEventListener("pointermove", onMove);
    target.addEventListener("pointerup", onUp);
  };

  // Drag/click directly on the focal-point thumbnail to pick which part of
  // the image stays visible when `fit` crops it — same idea as Canva's
  // "Reposition image" picker.
  const handleFocalPickerPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    const updateFromPoint = (clientX: number, clientY: number) => {
      const rect = target.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));
      setBackground((prev) => ({ ...prev, focalPoint: { x, y } }));
    };
    updateFromPoint(e.clientX, e.clientY);
    const onMove = (ev: PointerEvent) => updateFromPoint(ev.clientX, ev.clientY);
    const onUp = () => {
      target.removeEventListener("pointermove", onMove);
      target.removeEventListener("pointerup", onUp);
      setBackground((prev) => { logBackgroundPayload(prev); return prev; });
    };
    target.addEventListener("pointermove", onMove);
    target.addEventListener("pointerup", onUp);
  };

  // ── BACKGROUND / SAVE ─────────────────────────────────────────────────
  // Purely local: validate, compress, and preview via a blob: URL. Nothing
  // touches the server until Save actually happens.
  const handleBackgroundSelect = async (file: File) => {
    if (!ACCEPTED_BACKGROUND_IMAGE_TYPES.includes(file.type)) {
      setBackgroundError("Unsupported file type — use PNG, JPG, or WEBP.");
      return;
    }
    if (file.size > MAX_ORIGINAL_BACKGROUND_UPLOAD_SIZE) {
      setBackgroundError(`That image is too large to process — max ${Math.round(MAX_ORIGINAL_BACKGROUND_UPLOAD_SIZE / (1024 * 1024))}MB.`);
      return;
    }
    setBackgroundError(null);

    let compressed: { file: File; width: number; height: number };
    try {
      // Downscales to a sane max resolution and re-encodes as WebP at high
      // quality — most photos land well under the byte cap with no visible
      // quality loss, so this rarely trips the check below.
      compressed = await compressBackgroundImage(file);
    } catch {
      setBackgroundError("Couldn't read that image — please try another file.");
      return;
    }
    if (compressed.file.size > MAX_BACKGROUND_IMAGE_SIZE) {
      setBackgroundError(`That image is too large even after compression — max ${Math.round(MAX_BACKGROUND_IMAGE_SIZE / (1024 * 1024))}MB.`);
      return;
    }

    const objectUrl = URL.createObjectURL(compressed.file);
    setBackground((prev) => {
      if (prev.url.startsWith("blob:")) URL.revokeObjectURL(prev.url); // drop the previous pick, if any — never accumulate
      const next: DocumentBackground = {
        ...prev, url: objectUrl, width: compressed.width, height: compressed.height,
        focalPoint: { ...DEFAULT_FOCAL_POINT },
      };
      return next;
    });
    setPendingBackgroundFile(compressed.file);
  };

  const handleRemoveBackground = () => {
    setBackground((prev) => {
      if (prev.url.startsWith("blob:")) URL.revokeObjectURL(prev.url);
      const next = { ...prev, url: "" };
      logBackgroundPayload(next);
      return next;
    });
    setPendingBackgroundFile(null);
  };

  const handleOpacityCommit = (opacity: number) => {
    setBackground((prev) => {
      const next = { ...prev, opacity };
      logBackgroundPayload(next);
      return next;
    });
  };

  const applyPreset = (label: string) => {
    const preset = CANVAS_PRESETS.find((p) => p.label === label);
    if (preset) setBackground((prev) => {
      const next = { ...prev, width: preset.width, height: preset.height };
      logBackgroundPayload(next);
      return next;
    });
  };

  const handleFitChange = (fit: DocumentBackground["fit"]) => {
    setBackground((prev) => {
      const next = { ...prev, fit };
      logBackgroundPayload(next);
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      // The background only actually reaches Storage right here, at the
      // moment of a real save — never on file selection. If nothing new was
      // picked (background unchanged, or there was never one), this is
      // skipped entirely: zero upload calls.
      let finalBackground = background;
      if (pendingBackgroundFile) {
        const uploadResult = await service.current.uploadBackground(pendingBackgroundFile);
        if (!uploadResult.success) {
          setBackgroundError("Couldn't upload the background image — please try again.");
          return;
        }
        // Width/height are intentionally NOT taken from the upload result —
        // they were already captured locally on selection and may have been
        // deliberately resized/reshaped since; only the URL changes here.
        finalBackground = { ...background, url: uploadResult.url };
      }

      // `access` is intentionally omitted — the server defaults it to fully
      // open on create, and update never touches it (access is only ever
      // changed via AccessControlPanel's dedicated endpoints).
      const payload = {
        name: name.trim(),
        description: description.trim(),
        background: finalBackground,
        elements,
        isActive: template?.isActive ?? true,
      };
      const result = template
        ? await service.current.updateTemplate(template.id, payload)
        : await service.current.createTemplate(payload);
      if (result.success) {
        if (background.url.startsWith("blob:")) URL.revokeObjectURL(background.url);
        setPendingBackgroundFile(null);
        onSaved();
        onOpenChange(false);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[100vw] sm:max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] rounded-none p-4 gap-2 flex flex-col overflow-hidden border-border/60">
          <DialogHeader className="border-b border-border/40 pb-2 pr-12 shrink-0 flex-row items-center gap-4 space-y-0">
            <DialogTitle className="text-lg font-bold tracking-tight whitespace-nowrap">
              {template ? "Edit Template" : "New Template"}
            </DialogTitle>
            <div className="flex-1 grid grid-cols-2 gap-3">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name, e.g. Bonafide Certificate" className="h-8 text-sm" />
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional short description" className="h-8 text-sm" />
            </div>
          </DialogHeader>

          {/* TOOLBAR — always a single row; scrolls horizontally instead of
              wrapping at narrower desktop widths, so a wrapped second row
              never collides with the inspector panel below it. */}
          <div className="flex flex-nowrap items-center gap-2 py-1.5 border-y border-border/40 shrink-0 overflow-x-auto overflow-y-hidden">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_BACKGROUND_IMAGE_TYPES.join(",")}
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleBackgroundSelect(f); e.target.value = ""; }}
            />
            <Button variant="outline" size="sm" className="gap-1.5 text-xs font-bold" onClick={() => fileInputRef.current?.click()}>
              <ImageIcon className="h-3.5 w-3.5" />
              {background.url ? "Replace Background" : "Background"}
            </Button>
            {background.url && (
              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={handleRemoveBackground} title="Remove background image">
                <ImageOff className="h-3.5 w-3.5" />
              </Button>
            )}
            {backgroundError && (
              <span className="flex items-center gap-1 text-[11px] font-semibold text-red-600">
                <AlertTriangle className="h-3 w-3" /> {backgroundError}
              </span>
            )}

            <Select onValueChange={applyPreset}>
              <SelectTrigger className="h-8 w-[150px] text-xs"><SelectValue placeholder="Canvas size" /></SelectTrigger>
              <SelectContent>
                {CANVAS_PRESETS.map((p) => <SelectItem key={p.label} value={p.label}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-1">
              <Input
                type="number"
                min={100}
                value={Math.round(background.width)}
                onChange={(e) => { const v = Number(e.target.value); if (v > 0) setBackground((prev) => ({ ...prev, width: Math.max(100, v) })); }}
                className="h-8 w-16 text-xs"
                title="Custom width (px)"
              />
              <span className="text-xs text-muted-foreground">×</span>
              <Input
                type="number"
                min={100}
                value={Math.round(background.height)}
                onChange={(e) => { const v = Number(e.target.value); if (v > 0) setBackground((prev) => ({ ...prev, height: Math.max(100, v) })); }}
                className="h-8 w-16 text-xs"
                title="Custom height (px)"
              />
            </div>

            <Separator orientation="vertical" className="h-6" />

            <Button variant="outline" size="sm" className="gap-1.5 text-xs font-bold" onClick={addText}>
              <Type className="h-3.5 w-3.5" /> Text
            </Button>
            <Button variant="outline" size="sm" className="text-xs font-bold" onClick={() => addWildcard("user_name")}>{"{User Name}"}</Button>
            <Button variant="outline" size="sm" className="text-xs font-bold" onClick={() => addWildcard("user_email")}>{"{User Email}"}</Button>
            <Button variant="outline" size="sm" className="text-xs font-bold" onClick={() => addWildcard("user_id")}>{"{User ID}"}</Button>
            <Button variant="outline" size="sm" className="gap-1.5 text-xs font-bold" onClick={addLine}>
              <Minus className="h-3.5 w-3.5" /> Blank Line
            </Button>

            <Separator orientation="vertical" className="h-6" />

            <Button variant="outline" size="sm" className="gap-1.5 text-xs font-bold text-red-600" onClick={deleteSelected} disabled={!selected}>
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </Button>

            <div className="ml-auto flex items-center gap-2">
              <div className="flex items-center gap-0.5 rounded-md border border-border/60 p-0.5">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={zoomOut} disabled={zoom <= 0.25} title="Zoom out">
                  <ZoomOut className="h-3.5 w-3.5" />
                </Button>
                <button
                  type="button"
                  onClick={zoomReset}
                  className="w-14 text-center text-[11px] font-bold text-muted-foreground hover:text-foreground"
                  title="Reset zoom"
                >
                  {Math.round(zoom * 100)}%
                </button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={zoomIn} disabled={zoom >= 3} title="Zoom in">
                  <ZoomIn className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={zoomReset} title="Fit to screen">
                  <Maximize className="h-3.5 w-3.5" />
                </Button>
              </div>
              <Button variant="outline" size="sm" className="gap-1.5 text-xs font-bold" onClick={() => setPreviewOpen(true)}>
                <Eye className="h-3.5 w-3.5" /> Preview
              </Button>
            </div>
          </div>

          {/* CANVAS + INSPECTOR */}
          <div className="flex gap-4 py-1.5 flex-1 min-h-0">
            <div
              onWheel={handleCanvasWheel}
              className="overflow-auto border border-border/60 rounded-lg bg-muted/20 p-4 flex-1"
            >
              <div ref={canvasWrapRef} className="w-full h-full grid place-items-center">
              <div
                style={{
                  position: "relative",
                  width: background.width * scale,
                  height: background.height * scale,
                  background: "#ffffff",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                  outline: "1px solid rgba(0,0,0,0.08)",
                }}
                onPointerDown={() => setSelectedId(null)}
              >
                {/* Clipped to the page bounds — matches DocumentCanvasView's
                    read-only renderer exactly, so an element dragged/resized
                    outside the page is visibly cut off here too instead of
                    silently disappearing only once the document is actually
                    rendered/printed. Resize handles live outside this layer
                    so they're never clipped at the edges. */}
                <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
                  {background.url && <div style={{ ...backgroundLayerStyle(background), zIndex: 0 }} />}
                  {elements.slice().sort((a, b) => a.zIndex - b.zIndex).map((el) => (
                    <div
                      key={el.id}
                      onPointerDown={(e) => handleDragStart(e, el)}
                      style={{
                        position: "absolute",
                        left: el.x * scale,
                        top: el.y * scale,
                        width: el.width * scale,
                        height: el.height * scale,
                        zIndex: el.zIndex,
                        cursor: "move",
                        outline: selectedId === el.id ? "2px solid var(--primary, #2563eb)" : "1px dashed rgba(0,0,0,0.2)",
                      }}
                    >
                      {isTextLike(el) ? (
                        <div style={{
                          fontFamily: el.fontFamily, fontSize: el.fontSize * scale, fontWeight: el.fontWeight,
                          fontStyle: el.italic ? "italic" : "normal", color: el.color, textAlign: el.align,
                          width: "100%", height: "100%", display: "flex", alignItems: "center",
                          justifyContent: el.align === "left" ? "flex-start" : el.align === "right" ? "flex-end" : "center",
                          whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.2, pointerEvents: "none",
                        }}>
                          {el.type === "text" ? el.content : `{${el.wildcardKey === "user_name" ? "User Name" : el.wildcardKey === "user_email" ? "User Email" : "User ID"}}`}
                        </div>
                      ) : (
                        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", pointerEvents: "none" }}>
                          <div style={{ width: "100%", borderBottom: `2px solid ${el.color}` }} />
                        </div>
                      )}
                      {selectedId === el.id && (
                        <div
                          onPointerDown={(e) => handleResizeStart(e, el)}
                          style={{
                            position: "absolute", right: -6, bottom: -6, width: 12, height: 12,
                            borderRadius: 999, background: "#2563eb", cursor: "nwse-resize",
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {RESIZE_HANDLES.map((h) => (
                  <div
                    key={h.key}
                    onPointerDown={(e) => handleBackgroundHandleResize(e, h.key)}
                    title={h.corner ? "Drag to resize (keeps proportions)" : "Drag to reshape (stretches this side)"}
                    style={{
                      position: "absolute",
                      top: `${h.top}%`,
                      left: `${h.left}%`,
                      transform: "translate(-50%, -50%)",
                      width: h.corner ? 14 : (h.key === "n" || h.key === "s" ? 22 : 10),
                      height: h.corner ? 14 : (h.key === "n" || h.key === "s" ? 10 : 22),
                      borderRadius: h.corner ? 4 : 3,
                      background: h.corner ? "#f59e0b" : "#2563eb",
                      border: "2px solid white",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
                      cursor: h.cursor,
                      zIndex: 9999,
                    }}
                  />
                ))}
              </div>
              </div>
            </div>

            {/* INSPECTOR */}
            <div className="w-[280px] shrink-0 space-y-4 overflow-y-auto pr-1">
              {!selected ? (
                background.url ? (
                  <div className="space-y-4">
                    <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide">Background Image</p>
                    <div className="space-y-1.5">
                      <Label className="text-[11px] font-bold text-muted-foreground">Fit</Label>
                      <Select value={background.fit} onValueChange={(v) => handleFitChange(v as DocumentBackground["fit"])}>
                        <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cover">Cover (crop to fill)</SelectItem>
                          <SelectItem value="contain">Contain (fit whole image)</SelectItem>
                          <SelectItem value="fill">Stretch (fill exactly)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {background.fit !== "fill" && (
                      <div className="space-y-1.5">
                        <Label className="text-[11px] font-bold text-muted-foreground">
                          Focus Point ({Math.round(background.focalPoint.x)}%, {Math.round(background.focalPoint.y)}%)
                        </Label>
                        <div
                          onPointerDown={handleFocalPickerPointerDown}
                          className="relative w-full rounded-md border border-border/60 cursor-crosshair select-none overflow-hidden"
                          style={{
                            aspectRatio: `${background.width} / ${background.height}`,
                            backgroundImage: `url(${background.url})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              left: `${background.focalPoint.x}%`,
                              top: `${background.focalPoint.y}%`,
                              transform: "translate(-50%, -50%)",
                              width: 14, height: 14, borderRadius: 999,
                              background: "#2563eb", border: "2px solid white",
                              boxShadow: "0 0 0 1px rgba(0,0,0,0.4)",
                              pointerEvents: "none",
                            }}
                          />
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          Click or drag on the thumbnail to pick what stays visible when the image is cropped.
                        </p>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <Label className="text-[11px] font-bold text-muted-foreground">
                        Opacity ({Math.round(background.opacity)}%)
                      </Label>
                      <Slider
                        value={[ background.opacity ]}
                        min={0} max={100} step={5}
                        onValueChange={([ v ]) => setBackground((prev) => ({ ...prev, opacity: v }))}
                        onValueCommit={([ v ]) => handleOpacityCommit(v)}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Select an element to edit its properties.</p>
                )
              ) : selected.type === "line" ? (
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-muted-foreground">Line Color</Label>
                  <Input type="color" value={selected.color} onChange={(e) => updateElement(selected.id, { color: e.target.value })} className="h-9 p-1" />
                </div>
              ) : (
                <>
                  {selected.type === "text" && (
                    <div className="space-y-1.5">
                      <Label className="text-[11px] font-bold text-muted-foreground">Text</Label>
                      <Input value={selected.content} onChange={(e) => updateElement(selected.id, { content: e.target.value })} />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-muted-foreground">Font</Label>
                    <Select value={selected.fontFamily} onValueChange={(v) => updateElement(selected.id, { fontFamily: v })}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FONT_FAMILIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-muted-foreground">Size</Label>
                    <Input type="number" min={8} max={96} value={selected.fontSize} onChange={(e) => updateElement(selected.id, { fontSize: Number(e.target.value) || 16 })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-muted-foreground">Style</Label>
                    <div className="flex gap-1.5">
                      <Button size="icon" variant={selected.fontWeight === "bold" ? "default" : "outline"} className="h-9 w-9" onClick={() => updateElement(selected.id, { fontWeight: selected.fontWeight === "bold" ? "normal" : "bold" })}>
                        <Bold className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant={selected.italic ? "default" : "outline"} className="h-9 w-9" onClick={() => updateElement(selected.id, { italic: !selected.italic })}>
                        <Italic className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-muted-foreground">Align</Label>
                    <div className="flex gap-1.5">
                      <Button size="icon" variant={selected.align === "left" ? "default" : "outline"} className="h-9 w-9" onClick={() => updateElement(selected.id, { align: "left" })}><AlignLeft className="h-4 w-4" /></Button>
                      <Button size="icon" variant={selected.align === "center" ? "default" : "outline"} className="h-9 w-9" onClick={() => updateElement(selected.id, { align: "center" })}><AlignCenter className="h-4 w-4" /></Button>
                      <Button size="icon" variant={selected.align === "right" ? "default" : "outline"} className="h-9 w-9" onClick={() => updateElement(selected.id, { align: "right" })}><AlignRight className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-muted-foreground">Color</Label>
                    <Input type="color" value={selected.color} onChange={(e) => updateElement(selected.id, { color: e.target.value })} className="h-9 p-1" />
                  </div>
                </>
              )}
            </div>
          </div>

          <DialogFooter className="border-t border-border/40 pt-2 shrink-0">
            <Button variant="ghost" className="text-xs font-bold" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button className="min-w-[140px] text-xs font-bold" onClick={handleSave} disabled={saving || !name.trim()}>
              {saving ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Saving...</> : "Save Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PREVIEW */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-[100vw] sm:max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] rounded-none flex flex-col overflow-hidden border-border/60">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-lg font-bold">Preview — sample data</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-auto flex items-center justify-center py-2">
            <DocumentCanvasView background={background} elements={elements} wildcardValues={SAMPLE_PREVIEW_USER} containerId="doc-preview-print" />
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="outline" className="gap-1.5 text-xs font-bold" onClick={() => printElement("doc-preview-print", name || "Document Preview")}>
              <Printer className="h-3.5 w-3.5" /> Print / Save as PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
